import type { PackagingCategory } from '../../packaging/models/Packaging';
import type { CreatePackagingRequest } from '../../packaging/models/Packaging';
import type { PackagingDimensions, PackagingDimensionFormState } from '../types/dimensions';
import { EMPTY_DIMENSION_FORM } from '../types/dimensions';

const parseDimValue = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildDimensionsObject = (length: string, width: string, height: string): PackagingDimensions | undefined => {
  const l = parseDimValue(length);
  const w = parseDimValue(width);
  const h = parseDimValue(height);

  if (l === undefined && w === undefined && h === undefined) {
    return undefined;
  }

  return {
    ...(l !== undefined ? { length: l } : {}),
    ...(w !== undefined ? { width: w } : {}),
    ...(h !== undefined ? { height: h } : {}),
  };
};

export const dimensionFormFromItem = (
  folded?: PackagingDimensions | null,
  assembled?: PackagingDimensions | null,
  dimensionUnit?: string | null,
): PackagingDimensionFormState => ({
  foldedLength: folded?.length != null ? String(folded.length) : '',
  foldedWidth: folded?.width != null ? String(folded.width) : '',
  foldedHeight: folded?.height != null ? String(folded.height) : '',
  assembledLength: assembled?.length != null ? String(assembled.length) : '',
  assembledWidth: assembled?.width != null ? String(assembled.width) : '',
  assembledHeight: assembled?.height != null ? String(assembled.height) : '',
  dimensionUnit: dimensionUnit || 'cm',
});

export const dimensionsFormHasValues = (state: PackagingDimensionFormState): boolean => {
  return (
    state.foldedLength.trim() !== '' ||
    state.foldedWidth.trim() !== '' ||
    state.foldedHeight.trim() !== '' ||
    state.assembledLength.trim() !== '' ||
    state.assembledWidth.trim() !== '' ||
    state.assembledHeight.trim() !== '' ||
    (state.dimensionUnit.trim() !== '' && state.dimensionUnit !== 'cm')
  );
};

export const dimensionsFormChanged = (
  current: PackagingDimensionFormState,
  initial: PackagingDimensionFormState = EMPTY_DIMENSION_FORM,
): boolean => {
  return (
    current.foldedLength !== initial.foldedLength ||
    current.foldedWidth !== initial.foldedWidth ||
    current.foldedHeight !== initial.foldedHeight ||
    current.assembledLength !== initial.assembledLength ||
    current.assembledWidth !== initial.assembledWidth ||
    current.assembledHeight !== initial.assembledHeight ||
    current.dimensionUnit !== initial.dimensionUnit
  );
};

export const buildPackagingDimensionsPayload = (
  state: PackagingDimensionFormState,
  category: PackagingCategory,
  mode: 'create' | 'edit',
  initialState: PackagingDimensionFormState = EMPTY_DIMENSION_FORM,
): Pick<CreatePackagingRequest, 'folded_dimensions' | 'assembled_dimensions' | 'dimension_unit'> | undefined => {
  if (category === 'marketing_item') {
    return undefined;
  }

  const shouldInclude = mode === 'create' ? dimensionsFormHasValues(state) : dimensionsFormChanged(state, initialState);

  if (!shouldInclude) {
    return undefined;
  }

  const folded_dimensions = buildDimensionsObject(state.foldedLength, state.foldedWidth, state.foldedHeight);
  const assembled_dimensions = buildDimensionsObject(
    state.assembledLength,
    state.assembledWidth,
    state.assembledHeight,
  );

  const payload: Pick<CreatePackagingRequest, 'folded_dimensions' | 'assembled_dimensions' | 'dimension_unit'> = {};

  if (folded_dimensions) {
    payload.folded_dimensions = folded_dimensions;
  }
  if (assembled_dimensions) {
    payload.assembled_dimensions = assembled_dimensions;
  }
  if (state.dimensionUnit.trim()) {
    payload.dimension_unit = state.dimensionUnit.trim();
  }

  if (!payload.folded_dimensions && !payload.assembled_dimensions && !payload.dimension_unit) {
    return undefined;
  }

  return payload;
};

export const appendPackagingDimensionsToFormData = (
  formData: FormData,
  data: Pick<CreatePackagingRequest, 'folded_dimensions' | 'assembled_dimensions' | 'dimension_unit'>,
): void => {
  if (data.folded_dimensions) {
    if (data.folded_dimensions.length !== undefined) {
      formData.append('folded_dimensions[length]', String(data.folded_dimensions.length));
    }
    if (data.folded_dimensions.width !== undefined) {
      formData.append('folded_dimensions[width]', String(data.folded_dimensions.width));
    }
    if (data.folded_dimensions.height !== undefined) {
      formData.append('folded_dimensions[height]', String(data.folded_dimensions.height));
    }
  }

  if (data.assembled_dimensions) {
    if (data.assembled_dimensions.length !== undefined) {
      formData.append('assembled_dimensions[length]', String(data.assembled_dimensions.length));
    }
    if (data.assembled_dimensions.width !== undefined) {
      formData.append('assembled_dimensions[width]', String(data.assembled_dimensions.width));
    }
    if (data.assembled_dimensions.height !== undefined) {
      formData.append('assembled_dimensions[height]', String(data.assembled_dimensions.height));
    }
  }

  if (data.dimension_unit) {
    formData.append('dimension_unit', data.dimension_unit);
  }
};
